/**
 * Rule citations, everywhere, clickable.
 *
 * Every number this app produces traces to a rule, and the app is full of prose that names one —
 * "(2015-III-7)", "2020-II-1 §5 puts this at 5:00pm". Those used to be dead text while the chip
 * rows were interactive, which is exactly backwards: the inline mention is where somebody is
 * reading when the question occurs to them.
 *
 * So there are three pieces here:
 *   - a global modal, so a rule opens the same way from anywhere and reading one never scrolls
 *     the page out from under you;
 *   - `<Rule>`, an inline chip for a single citation;
 *   - `<Linkify>`, which walks a rendered subtree and turns any rule id it finds in plain text
 *     into one of those chips. Pages wrap themselves in it once, and prose stays prose.
 */

import {
  Fragment, createContext, isValidElement, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { useScrollLock } from './useScrollLock.js';
import ruleText from '../data/rule-text.json';
import rulebook from '../data/rulebook.json';
import {
  RULE_ANCHORS, amendmentChain, anchorDeclared, anchorTarget, relatedTo,
  screensFor,
} from '../domain/rulebook.js';
import { type RulebookAnchor, anchorHref } from '../domain/rulebookAnchors.js';
import { dock } from './dock.js';

type Rule = {
  id: string; year: number; section: string; title: string;
  status: string; summary: string; text: string;
};

/**
 * Two sources: the verbatim subset, and the full rulebook as a fallback so a citation to a rule
 * nobody has transcribed still opens with its summary rather than doing nothing.
 */
const VERBATIM = ruleText as Record<string, Rule | undefined>;
const ALL = Object.fromEntries(
  (rulebook as { rules: Rule[] }).rules.map((r) => [r.id, r]),
) as Record<string, Rule | undefined>;

export function ruleFor(id: string): Rule | undefined {
  return VERBATIM[id] ?? ALL[id];
}

export const isRuleId = (id: string) => Boolean(ruleFor(id));

/* -- the panel (3c) --------------------------------------------------------------------------
 *
 * A panel, not a tooltip, and the reasoning is in the canvas: a tooltip forces short text, and
 * short text is what produced the over-explanatory inline callouts this redesign removed. The
 * panel holds the rule VERBATIM once, says which screens enforce it, shows how it has been
 * amended, and offers the action it governs. The chip that opens it costs one line.
 *
 * It docks right on a desktop and bottom on a phone — the page stays where it was, because
 * reading a rule should never scroll the thing you were reading it about out from under you.
 */

const OpenCtx = createContext<(id: string) => void>(() => {});

/** A named set of rules the panel can show as an index — `{ title, rules }`. */
export interface RuleIndex {
  title: string;
  /**
   * The answer in prose, above the list. An index titled with a question and followed by four
   * rule titles is a reading list, not an answer; the note is the paragraph that says which of
   * them applies right now and why. Optional because most indexes are pure lists.
   */
  note?: string;
  rules: ReadonlyArray<{ id: string; what: string }>;
}
const IndexCtx = createContext<(index: RuleIndex) => void>(() => {});

/**
 * Where the panel sends you.
 *
 * The shell owns navigation and sits INSIDE this provider, so the handler is registered by the
 * shell on mount rather than threaded down as a prop. One app, one shell, one handler — a
 * module-level slot is honest about that and costs nothing.
 */
type Navigate = (section: string, page: string, ruleId?: string) => void;
let navigate: Navigate | null = null;
export function setRuleNavigator(fn: Navigate | null) {
  navigate = fn;
}

/**
 * The same slot, for any screen that needs to send somebody elsewhere — the draft-complete card's
 * "See 2027 keeper costs" is the first non-rule caller. Named for what it does rather than for
 * rules, since it stopped being only about rules the moment a second caller wanted it.
 */
export function goTo(section: string, page: string, ruleId?: string) {
  navigate?.(section, page, ruleId);
}

/*
 * `goToRule(id)` used to live here — `navigate('league', 'rules', id)` — and was the one thing in
 * the app that answered a `§` by changing the page. Both anchor marks now open the panel instead
 * (Tim, 22 August), so nothing calls it and it is gone rather than left as a loaded gun.
 *
 * The ROUTE it produced is still live and still wanted: `anchorHref` builds `#/league/rules/<id>`,
 * `Rules.tsx` opens on arrival, and the Calculator's marks keep it as their `href` so a rule can
 * be cmd-clicked into a Discord message. What went is the programmatic jump, and `goTo` above
 * covers it in one line if a screen ever genuinely wants to send somebody to the rulebook.
 */

export function RuleProvider({ children }: { children: ReactNode }) {
  const [id, setId] = useState<string | null>(null);
  /*
   * AN INDEX, IN THE SAME PANEL.
   *
   * A screen that enforces seven rules wants to show all seven, and the place a rule is read is
   * this panel — so the list is a state of the panel rather than a second list somewhere else on
   * the page. It was tried in the rail first: a 320px column set seven titles at four words a
   * line, which is a table of contents you have to decode. Here each row has the width to be a
   * sentence, and opening one is a step deeper rather than a jump elsewhere.
   *
   * `back` is what makes it a step: the index is remembered while a rule is open, so the way out
   * of the seventh rule is the list, not the screen.
   */
  const [index, setIndex] = useState<RuleIndex | null>(null);
  const open = useCallback((next: string) => setId(next), []);
  const openIndex = useCallback((next: RuleIndex) => { setIndex(next); setId(null); }, []);
  const close = useCallback(() => { setId(null); setIndex(null); }, []);
  const back = useCallback(() => setId(null), []);
  const rule = id ? ruleFor(id) : undefined;
  const showing = rule !== undefined || (index !== null && id === null);
  const governs = id ? screensFor(id) : [];
  const chain = id ? amendmentChain(id) : [];
  const related = id ? relatedTo(id) : [];
  const go = (section: string, page: string) => { close(); navigate?.(section, page); };

  /*
   * The page behind a panel does not move. Without this the panel's own scroller hands the swipe
   * on the moment it runs out, and a finger that lands on the panel's header or footer — neither
   * of which scrolls — moves the page instead of the rule.
   */
  useScrollLock(showing);

  useEffect(() => {
    if (!showing) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showing, close]);

  /*
   * BOTH PANELS ARE DOCKED (26 August). They are `position: fixed` and were rendered where they
   * sit in the tree — inside whatever page opened them, and therefore inside `main`, which
   * carries pull-to-refresh's transform. A transform makes its element the containing block for
   * every fixed descendant, so "fixed to the viewport" quietly meant "fixed to a page-height
   * column". Found on the keeper screen, where the submit sheet was landing 2,626px down an
   * 844px phone; see `src/ui/dock.tsx`.
   */
  return (
    <OpenCtx.Provider value={open}>
      <IndexCtx.Provider value={openIndex}>
      {children}
      {showing && !rule && index && dock(
        <>
          <button type="button" className="rulescrim" aria-label="Close" onClick={close} />
          <aside className="rulepanel" role="dialog" aria-modal="true" aria-label={index.title}>
            <header>
              <div>
                <span className="ruleid">{index.rules.length} rules</span>
                <h2>{index.title}</h2>
              </div>
              <button type="button" className="panelclose" aria-label="Close" onClick={close}>
                ×
              </button>
            </header>
            <div className="rulebody">
              {index.note && <p className="ruleindexnote">{index.note}</p>}
              <ol className="ruleindex">
                {index.rules.map((r, i) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => setId(r.id)}>
                      <span className="n">{i + 1}</span>
                      <span className="what">{r.what}</span>
                      <code>{r.id}</code>
                      <span className="go" aria-hidden="true">&rsaquo;</span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>
            <footer>
              <button type="button" className="btn" onClick={() => go('league', 'rules')}>
                Open full rulebook
              </button>
            </footer>
          </aside>
        </>,
      )}
      {rule && dock(
        <>
          <button type="button" className="rulescrim" aria-label="Close the rule"
            onClick={close} />
          <aside className="rulepanel" role="dialog" aria-modal="true" aria-label={rule.title}>
            {/*
              A BREADCRUMB ON ITS OWN LINE, above the header rather than inside it.
              Sharing the line with the rule id made two different things — where you came from
              and what you are looking at — read as one crowded label. On its own row with a rule
              under it, the panel says "you are one level down" before it says anything else.
              Only when this rule was reached FROM a list; otherwise there is nothing to go back to.
            */}
            {index && (
              <nav className="rulecrumb">
                <button type="button" className="rulesback" onClick={back}>
                  <span aria-hidden="true">&lsaquo;</span> {index.title}
                </button>
              </nav>
            )}
            <header className={index ? 'nested' : ''}>
              <div>
                <span className="ruleid">{rule.id}</span>
                <h2>{rule.title}</h2>
              </div>
              <button type="button" className="panelclose" aria-label="Close" onClick={close}>
                ×
              </button>
            </header>

            <div className="rulebody">
              {rule.status !== 'active' && (
                <p className={`rulestatus ${rule.status}`}>{rule.status.replace('-', ' ')}</p>
              )}
              <p className="rulesummary">{rule.summary}</p>
              <pre>{rule.text}</pre>
              {!VERBATIM[rule.id] && (
                <p className="note">
                  Text as recorded in the rulebook export rather than a verbatim transcription.
                </p>
              )}

              {governs.length > 0 && (
                <section>
                  <h3>Governs</h3>
                  <div className="govlinks">
                    {governs.map((g) => (
                      <button type="button" key={g.label} className="govpill"
                        onClick={() => go(g.section, g.page)}>
                        {g.label}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {related.length > 0 && (
                <section>
                  <h3>Related</h3>
                  <div className="cites">
                    {related.map((r) => (
                      <button type="button" key={r.id} className="cite" title={r.title}
                        onClick={() => setId(r.id)}>
                        {r.id}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {chain.length > 1 && (
                <section>
                  <h3>Amended</h3>
                  <ul className="amendlist">
                    {chain.map((a) => (
                      <li key={a.id} className={a.id === rule.id ? 'self' : ''}>
                        <span className="yr">{a.year}</span>
                        <span className="what">
                          {a.kind === 'adopted' ? 'Adopted' : a.kind === 'replaced' ? 'Replaced' : 'Superseded'}
                          {' — '}
                          {a.id === rule.id
                            ? a.title
                            : (
                              <button type="button" className="linkbtn"
                                onClick={() => setId(a.id)}>{a.title}</button>
                            )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            <footer>
              {governs[0] && (
                <button type="button" className="btn primary"
                  onClick={() => go(governs[0]!.section, governs[0]!.page)}>
                  {governs[0]!.action}
                </button>
              )}
              <button type="button" className="btn" onClick={() => go('league', 'rules')}>
                Open full rulebook
              </button>
            </footer>
          </aside>
        </>,
      )}
      </IndexCtx.Provider>
    </OpenCtx.Provider>
  );
}

export const useRuleModal = () => useContext(OpenCtx);
/** Open the panel on a LIST of rules — see the note in `RuleProvider`. */
export const useRuleIndex = () => useContext(IndexCtx);

/* -- inline citation ------------------------------------------------------------------------ */

/**
 * A rule reference in running prose: a mark you can press, not the id spelled out.
 *
 * `(2023-keeper-expansion-eight)` in the middle of a sentence is noise — nobody reads the id, and
 * it breaks the line it sits in. The mark carries the id on its tooltip and its accessible name,
 * and opens the panel, which is where the id actually matters.
 *
 * Citation ROWS still print ids — see `Citations`. There the id is the content.
 */
export function Rule({ id, children }: { id: string; children?: ReactNode }) {
  const open = useRuleModal();
  if (!ruleFor(id)) return <>{children ?? id}</>;
  const rule = ruleFor(id);
  return (
    <button type="button" className="rulemark" onClick={() => open(id)}
      aria-label={`Rule ${id}${rule?.title ? ` — ${rule.title}` : ''}`}
      title={`${id}${rule?.title ? ` — ${rule.title}` : ''}`}>
      <span aria-hidden="true">§</span>
    </button>
  );
}

/**
 * A `§` that opens a hand-built index — for the marks whose answer depends on the moment.
 *
 * "How the window works" on a live trade is a different set of rules in July, in October and on
 * draft night, and the sentence explaining which is in force has a date in it. That cannot be a
 * static anchor; the caller builds the index (`windowWhy`) and this mark opens it.
 */
export function IndexMark({ index, children }: { index: RuleIndex; children?: ReactNode }) {
  const openIndex = useRuleIndex();
  return (
    <button type="button" className="rulemark" onClick={() => openIndex(index)}
      aria-label={index.title} title={index.title}>
      <span aria-hidden="true">§</span>{children}
    </button>
  );
}

/**
 * What a `§` wired to an ANCHOR does when it is pressed — shared by the two marks that have one.
 *
 * One rule opens that rule. More than one opens the panel's index, titled with the question the
 * mark is asking, and picking from it opens that rule with a breadcrumb back to the list.
 *
 * Returns null when the anchor cannot resolve, which is how both marks know to render inert.
 */
function useAnchorOpener(anchorId: string): (() => void) | null {
  const openRule = useRuleModal();
  const openIndex = useRuleIndex();
  const target = anchorTarget(anchorId);
  if (!target) return null;
  const rules = target.rules.filter((r) => ruleFor(r.id));
  if (rules.length === 0) return null;
  return () => {
    /*
     * ONE RULE, NO LIST. A picklist of one is a step that asks a question with a single answer,
     * and the reader has already said which rule he wants by pressing a mark that means it.
     */
    if (rules.length === 1) { openRule(rules[0]!.id); return; }
    openIndex({
      title: target.label,
      rules: rules.map((r) => ({
        id: r.id,
        /*
         * The rule's own title, plus the section when the anchor names one. `2014-II-1 §1` is the
         * odds table inside a rule whose other four sections are about something else, and a row
         * that said only the rule's title would send the reader to the wrong half of it.
         */
        what: `${ruleFor(r.id)?.title ?? r.id}${r.section ? ` §${r.section}` : ''}`,
      })),
    });
  };
}

/**
 * The same mark, pointed at an ANCHOR rather than at one rule.
 *
 * Several `§` marks in this app are not citations to a whole rule. "How the lottery works" is TWO
 * rules — one creates the draw, the other changed what winning it gets you — and neither alone is
 * the answer. "The sleeper reward" is five. `RULE_ANCHORS` in `domain/rulebook.ts` declares them.
 *
 * IT OPENS THE PANEL, LIKE EVERY OTHER `§` (Tim, 22 August).
 *
 * This used to navigate: `goToRule(anchor.rules[0])` sent the reader to League › Rules with the
 * first rule of the anchor open. That was written while the panel could not show a set, and it is
 * the wrong shape twice over. It answers "how does the lottery work" by taking the page away from
 * under you — you were mid-way through setting your draft preferences — and it silently picks ONE
 * of the rules and drops the rest, which is exactly the thing an anchor exists to avoid.
 *
 * So: one rule opens that rule. More than one opens the panel's INDEX, titled with the question
 * the mark is asking, and picking from it opens that rule with a breadcrumb back to the list —
 * `RuleProvider` already keeps the index while a rule is open, so the way out of the seventh rule
 * is the list rather than the screen. None of that machinery is new; it was built for the index
 * and this mark was never wired to it.
 *
 * INERT, NOT ABSENT, when an anchor cannot resolve — and not `disabled` either. Leaving the mark
 * out would change the layout; `disabled` would drop it out of the tab order and read to a screen
 * reader as a control that is temporarily off rather than one that is not built. It announces
 * itself as unavailable and says why.
 */
export function AnchorMark({ anchor, size = 'md' }: { anchor: string; size?: 'md' | 'sm' }) {
  const press = useAnchorOpener(anchor);
  if (!anchorDeclared(anchor)) {
    // A `§` pointing nowhere is the failure this registry exists to make impossible to ship.
    throw new Error(`AnchorMark: no anchor declared for "${anchor}" — add it to RULE_ANCHORS.`);
  }
  const label = RULE_ANCHORS[anchor]?.label ?? anchor;
  return (
    <button
      type="button"
      className={`rulemark anchor ${size}`}
      aria-disabled={press ? undefined : true}
      aria-label={press ? label : `${label} — not in the rulebook yet`}
      title={press ? label : `${label} — rulebook destination not built yet (${anchor})`}
      onClick={() => press?.()}
    >
      <span aria-hidden="true">§</span>
    </button>
  );
}

/** A row of citation chips. */
/**
 * The rules behind something, as marks rather than ids.
 *
 * A row reading `2023-keeper-expansion-eight · 2019-II-1 · 2015-III-7` is a row nobody reads; the
 * id matters once the panel is open, and the panel still prints it. Each mark carries its rule's
 * id and title on the tooltip. An id with no rule behind it stays as text — it is the one case
 * where the string itself is the information, because somebody has to go and find it.
 */
export function Citations({ ids }: { ids: string[] }) {
  const known = ids.filter((id) => ruleFor(id));
  const unknown = ids.filter((id) => !ruleFor(id));
  if (!known.length && !unknown.length) return null;

  return (
    <div className="cites-wrap">
      <div className="cites">
        <span className="citelabel">Rules</span>
        {known.map((id) => <Rule key={id} id={id} />)}
        {unknown.map((id) => <span className="cite dim" key={id} title="Not in the rulebook export">{id}</span>)}
      </div>
    </div>
  );
}

/* -- a citation to a SECTION of the rulebook, not to one rule ------------------------------- */

/**
 * The `§` that points at a Rulebook section rather than at a single rule.
 *
 * `<Rule>` above opens the panel on one rule, which is right when one rule is the answer. It is
 * wrong when the answer is a rule and its amendments read together: "the sleeper reward" is
 * 2015-III-11 as amended in 2016, 2017 and 2022, and a mark that opened the 2017 text alone would
 * hand a manager two thirds of the rule with nothing to say the rest exists. Those destinations
 * are sections of a Rulebook page that has not been built — see `domain/rulebookAnchors.ts`.
 *
 * IT OPENS THE PANEL NOW, LIKE EVERY OTHER `§` (Tim, 22 August). All four of the Calculator's
 * anchors are multi-rule — keeper cost is five rules, the luxury tax six — so pressing one opens
 * the picklist, and picking from it opens that rule with a way back to the list. Sending the
 * reader to League › Rules instead took the Calculator away from under him to answer a question
 * about the number he was looking at, and silently picked one of the five rules to show him.
 *
 * THE `href` STAYS, and that is the reason this is still an anchor rather than a button. It is a
 * real route — `#/league/rules/2017-II-1` — so cmd-click, middle-click and "copy link" all do the
 * useful thing and put a rule in somebody's Discord message. Only a plain left click is taken over.
 *
 * IT IS STILL A 44px TARGET. A finger that misses tells a manager the app is broken, and the size
 * is the thing that would otherwise be quietly forgotten.
 */
export function RuleAnchor(
  { anchor, label, className }:
  { anchor: RulebookAnchor; label?: string; className?: string },
) {
  const press = useAnchorOpener(anchor.id);
  return (
    <a
      className={`kcmark${className ? ` ${className}` : ''}`}
      href={anchorHref(anchor)}
      aria-label={label ? undefined : anchor.label}
      title={anchor.pending ? `${anchor.label} — not in the rulebook yet` : anchor.label}
      data-rulebook-anchor={anchor.id}
      {...(anchor.pending ? { 'data-rulebook-pending': '' } : {})}
      onClick={(e) => {
        // A modified click is the reader asking for the LINK. Leave it to the browser.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        press?.();
      }}
    >
      {label ? <>{label} <span aria-hidden="true">&sect;</span></> : <span aria-hidden="true">&sect;</span>}
    </a>
  );
}

/* -- automatic linking ---------------------------------------------------------------------- */

/**
 * Matches the two shapes the league writes: `2019-II-1` and the unnumbered 2022/23 style
 * `2023-in-season-wagering`. A trailing section marker is pulled in so "2020-II-1 §5" reads as one
 * citation rather than a chip followed by an orphan §5.
 */
const RULE_RE = /\b(\d{4}-(?:[IVX]+-\d+|[a-z][a-z0-9-]{3,}))(\s*§+\s*\d+(?:\s*[-–]\s*§?\d+)?)?/g;

function linkifyString(text: string, keyBase: string): ReactNode {
  RULE_RE.lastIndex = 0;
  if (!RULE_RE.test(text)) return text;
  RULE_RE.lastIndex = 0;

  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null = RULE_RE.exec(text);
  let n = 0;
  while (m) {
    const [whole, id] = m;
    if (ruleFor(id!)) {
      /*
       * The id is replaced by a mark, so the punctuation that used to wrap it has to go with it:
       * "(2019-II-1)" would otherwise leave "( § )", and a leading space would leave a gap before
       * a full stop. Only the wrapper is swallowed — everything else in the sentence stands.
       */
      let from = m.index;
      let to = m.index + whole.length;
      const wrapped = (text[from - 1] === '(' && text[to] === ')')
        || (text[from - 1] === '[' && text[to] === ']');
      if (wrapped) { from -= 1; to += 1; }
      if (text[from - 1] === ' ' && (wrapped || text[to] === undefined || /[\s.,;:)]/.test(text[to]!))) {
        from -= 1;
      }
      if (from > last) out.push(text.slice(last, from));
      out.push(<Rule key={`${keyBase}-${n}`} id={id!} />);
      last = to;
      n += 1;
    }
    m = RULE_RE.exec(text);
  }
  if (!n) return text;
  if (last < text.length) out.push(text.slice(last));
  return <>{out.map((x, i) => <Fragment key={i}>{x}</Fragment>)}</>;
}

/**
 * Walk a rendered subtree and make every rule id in its text clickable.
 *
 * It only ever rewrites STRING children, and it leaves anything that is already a citation
 * component alone, so the worst case is that a mention is missed rather than a component broken.
 * Elements a child component builds internally are out of reach — those pages call `<Rule>`
 * directly where it matters.
 */
export function Linkify({ children }: { children: ReactNode }): ReactNode {
  return useMemo(() => walk(children, 'lk'), [children]);
}

function walk(node: ReactNode, key: string): ReactNode {
  if (typeof node === 'string') return linkifyString(node, key);
  if (Array.isArray(node)) {
    return node.map((child, i) => <Fragment key={i}>{walk(child, `${key}-${i}`)}</Fragment>);
  }
  if (!isValidElement(node)) return node;

  // Never descend into something whose whole job is citations, or into preformatted rule text.
  if (node.type === Rule || node.type === AnchorMark || node.type === Citations
    || node.type === 'pre') return node;

  /*
   * AN EXPLICIT OPT-OUT, for the one place where the id IS the content.
   *
   * The house rule is that a rule reference in prose becomes a `§` that opens the panel, because
   * `(2023-keeper-expansion-eight)` mid-sentence is noise nobody reads. The Rulebook page is the
   * exception on the same reasoning `Citations` already is: you are there to find a rule, the id
   * is its name, and a list of identical `§` marks tells you nothing about which row is which.
   *
   * Marked on the element rather than decided here, so the rule stays "prose gets a mark" and the
   * exceptions are visible where they are taken.
   */
  if ((node.props as { 'data-nolink'?: unknown })?.['data-nolink'] !== undefined) return node;

  const props = node.props as { children?: ReactNode };
  if (props?.children === undefined) return node;
  const next = walk(props.children, key);
  return next === props.children ? node : { ...node, props: { ...props, children: next } };
}
